import { useState } from 'react';
import { useSuspenseQuery } from '@tanstack/react-query';
import _ from 'lodash';
import {
  AppWindowIcon,
  ArrowsSplitIcon,
  BrowsersIcon,
  ChartBarHorizontalIcon,
  CityIcon,
  CrosshairIcon,
  DevicesIcon,
  EyeIcon,
  FlagIcon,
  FrameCornersIcon,
  FunnelIcon,
  PaperPlaneRightIcon,
  SignInIcon,
} from '@phosphor-icons/react';
import { Button } from '@logspot-platform/ui/button';
import { EmptyState } from '@logspot-platform/ui/empty-state';
import { aggregationByPropertyOptions } from './api/query-options';
import { Widget } from './widget';
import { acquisitionChannelLabel } from '@logspot-platform/platform-types';
import { useWidgetsContext } from './widgets-context';
import type { AggregationWidgetProps, EventData } from './types';

const getIcon = (property: string | undefined) => {
  if (property === '_url') {
    return <EyeIcon className="text-primary" />;
  }
  if (property === '_referrer') {
    return <PaperPlaneRightIcon className="text-primary" />;
  }
  if (property === '_location_country') {
    return <FlagIcon className="text-primary" />;
  }
  if (property === '_location_city') {
    return <CityIcon className="text-primary" />;
  }
  if (property === '_screen') {
    return <FrameCornersIcon className="text-primary" />;
  }
  if (property === '_device') {
    return <DevicesIcon className="text-primary" />;
  }
  if (property === '_browser') {
    return <BrowsersIcon className="text-primary" />;
  }
  if (property === '_os') {
    return <AppWindowIcon className="text-primary" />;
  }
  if (property === 'utm_source') {
    return <CrosshairIcon className="text-primary" />;
  }
  if (property === '_acquisition_channel') {
    return <ArrowsSplitIcon className="text-primary" />;
  }
  if (property === '_entry_url') {
    return <SignInIcon className="text-primary" />;
  }
  return <ChartBarHorizontalIcon className="text-primary" />;
};

// Entry page is a per-session argMin, not a row-level value, so it cannot go in
// a WHERE clause the way `_acquisition_channel` now can.
const UNFILTERABLE_FIELDS = new Set(['_entry_url']);

// Channel rows arrive as stored keys (`ai_assistant`); the vocabulary owns the
// display name so every surface shows the same one.
const displayValue = (
  dimension: string | undefined,
  value: string | undefined,
) => {
  if (!value) return 'No Value';
  if (dimension === '_acquisition_channel') {
    return acquisitionChannelLabel(value);
  }
  return value;
};

const PAGE_SIZE = 6;
export const AggregationWidget = ({
  renderProperty,
  renderOptions,
  renderToggle,
  renderToggledContent,
  filterData,
  // internal,
  dateRange,
  params,
  onDelete,
  transformData,
  transformKey,
}: AggregationWidgetProps) => {
  const { api, projectId, filterGroups, addFilter, isActiveFilter } =
    useWidgetsContext();
  const [toggled, setToggled] = useState(false);
  const [page, setPage] = useState(0);
  const isField = params?.property?.[0] === '_';
  const property = params?.property;
  const isDerived = UNFILTERABLE_FIELDS.has(property ?? '');
  const title = params?.title;
  const eventName = params?.eventName;
  const searchField = isField ? property?.slice(1, property?.length) : property;

  const { data } = useSuspenseQuery(
    aggregationByPropertyOptions({
      api,
      projectId,
      dateRange,
      eventName,
      property,
      filterGroups,
      transformData,
      transformKey,
    }),
  );

  let cleanedUp: EventData[] = [];

  if (filterData) {
    const result = filterData(data);
    if (Array.isArray(result)) {
      cleanedUp = result;
    } else {
      console.error('filterData did not return an array');
    }
  } else {
    cleanedUp = _.filter(
      data,
      (d) => !_.isNil(d.property) && !_.isEmpty(d.property),
    );
  }
  const max =
    cleanedUp.length > 0 ? parseInt(String(cleanedUp[0]?.count)) : null;

  return (
    <Widget
      Header={() => (
        <>
          <div className="flex items-center justify-between">
            <span className="flex items-center gap-2 text-xl font-medium">
              {getIcon(property)}
              <span className="font-semibold">{title}</span>
              {renderToggle && (
                <span
                  className="font-semibold"
                  onClick={() => setToggled(!toggled)}
                >
                  {renderToggle(toggled)}
                </span>
              )}
            </span>
            {(!renderToggle || !toggled) && (
              <div className="flex gap-2">
                <Button
                  disabled={page === 0}
                  size="sm"
                  variant="outline"
                  onClick={() => {
                    if (page > 0) {
                      setPage(page - 1);
                    }
                  }}
                >
                  Prev
                </Button>
                <Button
                  disabled={page * PAGE_SIZE + PAGE_SIZE > cleanedUp?.length}
                  size="sm"
                  variant="outline"
                  onClick={() => {
                    if (page * PAGE_SIZE + PAGE_SIZE < cleanedUp?.length) {
                      setPage(page + 1);
                    }
                  }}
                >
                  Next
                </Button>
              </div>
            )}
          </div>
          {renderOptions && (
            <div className="mt-3 flex items-center justify-between">
              {renderOptions()}
            </div>
          )}
        </>
      )}
      onDelete={onDelete}
    >
      <div className="flex h-full min-h-37.5 flex-col">
        {!cleanedUp?.length && <EmptyState headText="No Results Found" />}
        {renderToggle &&
          toggled &&
          renderToggledContent &&
          renderToggledContent(cleanedUp)}
        {(!renderToggle || !toggled) &&
          cleanedUp
            ?.slice(page * PAGE_SIZE, page * PAGE_SIZE + PAGE_SIZE)
            ?.map(({ property, count }) => {
              const filter = {
                eventName,
                field: searchField,
                value: property === '(None)' ? null : property,
                operator: '=',
              };
              const active = isActiveFilter ? isActiveFilter(filter) : false;
              return (
                <div key={property} className={`group flex items-center px-3`}>
                  {renderProperty ? (
                    <div className="relative mb-2 w-4/5 truncate py-1">
                      <div
                        className="bg-primary/30 absolute top-0 z-0 mr-3 h-full rounded align-middle"
                        style={{
                          width: max
                            ? `${(parseInt(String(count)) * 100) / max}%`
                            : '0%',
                        }}
                      ></div>
                      {renderProperty(property ?? '')}
                    </div>
                  ) : (
                    <div className="relative mb-2 w-4/5 truncate py-1">
                      <div
                        className="bg-primary/30 absolute top-0 z-0 mr-3 size-full rounded align-middle"
                        style={{
                          width: max
                            ? `${(parseInt(String(count)) * 100) / max}%`
                            : '0%',
                        }}
                      ></div>
                      <span className="relative z-10 pl-2 font-semibold">
                        {displayValue(params?.property, property)}
                      </span>
                    </div>
                  )}
                  <span className="w-1/5 text-right">
                    {parseInt(String(count))}
                  </span>
                  {active && (
                    <div className="bg-primary mx-2 flex items-center gap-1 rounded px-2 py-1 font-semibold text-white transition-all">
                      <FunnelIcon /> <span>Active</span>
                    </div>
                  )}
                  {!active && addFilter && !isDerived && (
                    <div
                      className="bg-primary hover:bg-primary/90 mx-2 hidden cursor-pointer items-center gap-1 rounded px-2 py-1 font-semibold text-white transition-all group-hover:flex"
                      onClick={() => addFilter(filter)}
                    >
                      <FunnelIcon /> <span>Filter</span>
                    </div>
                  )}
                </div>
              );
            })}
      </div>
    </Widget>
  );
};
